"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Typography } from "@repo/ui/aops-components/typography";
import { CourseSection } from "@/app/components/CourseSection";
import styles from "./LearningFormat.module.css";
import { useAtom } from "jotai";
import { learningFormatAtom, selectedCampusAtom, campusResolutionSettledAtom, selectedModalitySlugAtom } from "@/app/atoms/courseSelections";
import { useHydrateAtoms } from "jotai/utils";
import { SelectableTile } from "../SelectableTile";
import { Accordion } from "@repo/ui/mantine";
import { Minus, Plus } from "lucide-react";
import { LearningFormatMobile } from "./LearningFormatMobile";
import { EmptyFormatColumn } from "./EmptyFormatColumn";
import { FALLBACK_SUBTITLE, FALLBACK_TITLE, HIDE_DETAILS_KEY, LEARN_MORE_KEY, LEARN_MORE_VALUE, FALLBACK_LEARN_MORE_TITLE, FALLBACK_HIDE_DETAILS_TEXT, LearningFormatBestFor, LearningFormatDetailRow, LearningFormatHeader, LearningFormatQuickFact, LearningFormatWhyChoose, StickyFormatChrome, isBeastAcademyModality, useLinkToBAO, useModalityRows, type LearningFormatProps, STEP_TAG_KEY, FALLBACK_STEP_TAG, type Modality } from "./LearningFormatParts";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import { getModalityIdBySlug } from "@/lib/learningFormatData";
import { resolvePreferredModalitySlug, shouldApplyUrlPreference } from "../courseSelectionLogic";
import { getFormatAutoScrollTarget, scrollToCourseSection, shouldAutoScrollOnSelection } from "../scrollToCourseSection";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { didOpenLearnMore, getLearningFormatSectionCopy, getSelectedModalitySlug } from "../LearningFormatSelectionUtils";
export type { LearningFormatProps } from "./LearningFormatParts";
const EMPTY_KEYED_COPY: {
  key: string;
  text: string;
}[] = [];

/** Stacked single-column layout below desktop multi-column (Figma tablet ≤800). */
const STACKED_LAYOUT_MQ = "(max-width: 1023px)";
function subscribeStackedLayout(onStoreChange: () => void) {
  const mql = window.matchMedia(STACKED_LAYOUT_MQ);
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
}
function getStackedLayoutSnapshot() {
  return window.matchMedia(STACKED_LAYOUT_MQ).matches;
}
function useIsStackedLearningFormat() {
  return useSyncExternalStore(subscribeStackedLayout, getStackedLayoutSnapshot, _temp);
}
function _temp() {
  return false;
}
type SelectLearningFormat = (modality: Modality) => void;

/**
 * Multi-column Learning Format (≥768px). Shared subgrid + one section-level
 * Learn more accordion that expands detail rows across all columns.
 */
function LearningFormatDesktop(t0) {
  const $ = _c(66);
  const {
    campuses,
    campusCoursePrices,
    course,
    coursePageSection,
    campusLocationCoursePageSection,
    initialLearnMoreOpen: t1,
    linkToBAO,
    onSelectLearningFormat
  } = t0;
  const initialLearnMoreOpen = t1 === undefined ? false : t1;
  const [selectedLearningFormat] = useAtom(learningFormatAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  let t2;
  if ($[0] !== initialLearnMoreOpen) {
    t2 = () => initialLearnMoreOpen ? LEARN_MORE_VALUE : null;
    $[0] = initialLearnMoreOpen;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [openedAccordion, setOpenedAccordion] = useState(t2);
  let t3;
  if ($[2] !== coursePageSection?.keyedCopy) {
    t3 = coursePageSection?.keyedCopy ?? [];
    $[2] = coursePageSection?.keyedCopy;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const modalityRows = useModalityRows(course, selectedLearningFormat, campuses, selectedCampusId, campusCoursePrices, t3, campusLocationCoursePageSection);
  const isLearnMoreOpen = openedAccordion === LEARN_MORE_VALUE;
  let t4;
  if ($[4] !== modalityRows) {
    t4 = modalityRows.some(_temp2);
    $[4] = modalityRows;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const showBestForRow = t4;
  const maxQuickFactCount = modalityRows.reduce(_temp3, 0);
  const tileRows = 2 + (showBestForRow ? 1 : 0) + maxQuickFactCount;
  let t5;
  if ($[6] !== coursePageSection?.keyedCopy) {
    t5 = coursePageSection?.keyedCopy ?? [];
    $[6] = coursePageSection?.keyedCopy;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const keyedCopy = t5;
  let t6;
  if ($[8] !== course?.slug || $[9] !== isLearnMoreOpen || $[10] !== keyedCopy || $[11] !== linkToBAO || $[12] !== maxQuickFactCount || $[13] !== modalityRows || $[14] !== onSelectLearningFormat || $[15] !== showBestForRow) {
    t6 = () => modalityRows.map(t7 => {
      const {
        modality,
        modalityOffering,
        priceLines,
        quickFacts,
        isSelectDisabled,
        isSelected
      } = t7;
      return {
        value: modality._id,
        disabled: isSelectDisabled,
        body: <SelectableTile isSelected={isSelected} disabled={isSelectDisabled}><StickyFormatChrome stickyEnabled={isLearnMoreOpen}>{isStuck => <><SelectableTile.Content className={styles.formatHeaderContent}><LearningFormatHeader modality={modality} modalityOffering={modalityOffering} priceLines={priceLines} isCompact={isStuck} /></SelectableTile.Content><SelectableTile.SelectButton isSelected={isSelected} disabled={isSelectDisabled} onClick={() => {
                sendDiscoveryEventBeacon("course_modality_selected", {
                  course: course?.slug.current ?? "",
                  modality: modality.slug.current,
                  auto_select: false
                });
                if (isBeastAcademyModality(modality)) {
                  linkToBAO();
                }
                onSelectLearningFormat(modality);
              }} /></>}</StickyFormatChrome><SelectableTile.Content className={isLearnMoreOpen ? styles.formatWhyChoose : undefined}><LearningFormatWhyChoose modality={modality} keyedCopy={keyedCopy} />{modality.modalityFootnote && <Typography variant="bodySmall" className={styles.modalityFootnote}>{modality.modalityFootnote}</Typography>}</SelectableTile.Content>{showBestForRow ? <LearningFormatDetailRow expanded={isLearnMoreOpen}><LearningFormatBestFor modality={modality} keyedCopy={keyedCopy} /></LearningFormatDetailRow> : null}{Array.from({
            length: maxQuickFactCount
          }, (_, factIndex) => {
            const fact = quickFacts[factIndex];
            return <LearningFormatDetailRow key={fact?._key ?? `quick-fact-${factIndex}`} expanded={isLearnMoreOpen}>{fact ? <LearningFormatQuickFact fact={fact} /> : null}</LearningFormatDetailRow>;
          })}</SelectableTile>
      };
    });
    $[8] = course?.slug;
    $[9] = isLearnMoreOpen;
    $[10] = keyedCopy;
    $[11] = linkToBAO;
    $[12] = maxQuickFactCount;
    $[13] = modalityRows;
    $[14] = onSelectLearningFormat;
    $[15] = showBestForRow;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  course?.slug;
  let t7;
  if ($[17] !== t6) {
    t7 = t6();
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const options = t7;
  const t8 = coursePageSection?.title;
  const t9 = coursePageSection?.body;
  const t10 = modalityRows[0]?.modality.slug.current ?? undefined;
  let t11;
  if ($[19] !== keyedCopy || $[20] !== modalityRows.length || $[21] !== selectedCampusId || $[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = getLearningFormatSectionCopy({
      sectionTitle: t8,
      sectionBody: t9,
      keyedCopy,
      modalityCount: modalityRows.length,
      modalitySlug: t10,
      selectedCampusId,
      defaultTitle: FALLBACK_TITLE,
      defaultSubtitle: FALLBACK_SUBTITLE
    });
    $[19] = keyedCopy;
    $[20] = modalityRows.length;
    $[21] = selectedCampusId;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const {
    title,
    subtitle
  } = t11;
  const showEmptyColumn = options.length === 1;
  let t12;
  if ($[26] !== keyedCopy) {
    t12 = getKeyedCopyText(keyedCopy, LEARN_MORE_KEY) || FALLBACK_LEARN_MORE_TITLE;
    $[26] = keyedCopy;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const learnMoreText = t12;
  let t13;
  if ($[28] !== keyedCopy) {
    t13 = getKeyedCopyText(keyedCopy, HIDE_DETAILS_KEY) || FALLBACK_HIDE_DETAILS_TEXT;
    $[28] = keyedCopy;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const hideDetailsText = t13;
  let t14;
  if ($[30] !== keyedCopy) {
    t14 = getKeyedCopyText(keyedCopy, STEP_TAG_KEY) || FALLBACK_STEP_TAG;
    $[30] = keyedCopy;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const stepTag = t14;
  if (!options.length) {
    return null;
  }
  let t15;
  if ($[32] !== showEmptyColumn) {
    t15 = showEmptyColumn ? <EmptyFormatColumn /> : undefined;
    $[32] = showEmptyColumn;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== course?.slug || $[35] !== linkToBAO || $[36] !== modalityRows || $[37] !== onSelectLearningFormat) {
    t16 = value => {
      const modality_0 = modalityRows.find(row_1 => row_1.modality._id === value)?.modality;
      if (!modality_0) {
        return;
      }
      sendDiscoveryEventBeacon("course_modality_selected", {
        course: course?.slug.current ?? "",
        modality: modality_0.slug.current,
        auto_select: false
      });
      if (isBeastAcademyModality(modality_0)) {
        linkToBAO();
      }
      onSelectLearningFormat(modality_0);
    };
    $[34] = course?.slug;
    $[35] = linkToBAO;
    $[36] = modalityRows;
    $[37] = onSelectLearningFormat;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== course?.slug || $[40] !== modalityRows || $[41] !== openedAccordion) {
    t17 = value_0 => {
      if (didOpenLearnMore(value_0, openedAccordion, LEARN_MORE_VALUE)) {
        const modalitySlug = getSelectedModalitySlug(modalityRows);
        if (modalitySlug) {
          sendDiscoveryEventBeacon("course_modality_details_viewed", {
            course: course?.slug.current ?? "",
            modality: modalitySlug
          });
        }
      }
      setOpenedAccordion(value_0);
    };
    $[39] = course?.slug;
    $[40] = modalityRows;
    $[41] = openedAccordion;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      root: styles.accordionRoot,
      item: styles.accordionItem,
      control: styles.accordionControl,
      label: styles.accordionLabel
    };
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const t19 = isLearnMoreOpen ? hideDetailsText : learnMoreText;
  let t20;
  if ($[44] !== t19) {
    t20 = <Typography variant="buttonMd" as="span">{t19}</Typography>;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== isLearnMoreOpen) {
    t21 = isLearnMoreOpen ? <Minus size={24} strokeWidth={1.5} aria-hidden={true} /> : <Plus size={24} strokeWidth={1.5} aria-hidden={true} />;
    $[46] = isLearnMoreOpen;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t20 || $[49] !== t21) {
    t22 = <Accordion.Item value={LEARN_MORE_VALUE}><Accordion.Control><span className={styles.accordionControlInner}>{t20}{t21}</span></Accordion.Control></Accordion.Item>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== openedAccordion || $[52] !== t17 || $[53] !== t22) {
    t23 = <Accordion variant="unstyled" value={openedAccordion} onChange={t17} chevron={null} classNames={t18}>{t22}</Accordion>;
    $[51] = openedAccordion;
    $[52] = t17;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== options || $[56] !== selectedLearningFormat || $[57] !== showEmptyColumn || $[58] !== stepTag || $[59] !== subtitle || $[60] !== t15 || $[61] !== t16 || $[62] !== t23 || $[63] !== tileRows || $[64] !== title) {
    t24 = <CourseSection id="formats" title={title} subtitle={subtitle} stepTag={stepTag} options={options} tileRows={tileRows} fillTileWidth={showEmptyColumn} aside={t15} defaultValue={selectedLearningFormat} onSelect={t16} footer={t23} />;
    $[55] = options;
    $[56] = selectedLearningFormat;
    $[57] = showEmptyColumn;
    $[58] = stepTag;
    $[59] = subtitle;
    $[60] = t15;
    $[61] = t16;
    $[62] = t23;
    $[63] = tileRows;
    $[64] = title;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
}

/**
 * Learning formats (modalities) for a course.
 * Desktop (1024px+): multi-column subgrid with a shared Learn more accordion.
 * Tablet/phone (<1024px): single column; each option has its own Learn more
 * accordion. On phone (<768px), whyChoose also moves into that accordion.
 *
 * Modality preference persists in sessionStorage for the tab session. URL
 * `?modality=` wins over the session value. Clearing a campus-disabled
 * selection does not clear the session slug (so later courses can still use it).
 */
function _temp3(max, row_0) {
  return Math.max(max, row_0.quickFacts.length);
}
function _temp2(row) {
  return (row.modality.bestFor?.length ?? 0) > 0;
}
export function LearningFormat(props) {
  const $ = _c(52);
  let t0;
  if ($[0] !== props.course || $[1] !== props.initialModalitySlug) {
    t0 = getModalityIdBySlug(props.course, props.initialModalitySlug);
    $[0] = props.course;
    $[1] = props.initialModalitySlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const initialLearningFormatId = t0;
  let t1;
  if ($[3] !== props.initialSelectedCampusId) {
    t1 = [selectedCampusAtom, props.initialSelectedCampusId];
    $[3] = props.initialSelectedCampusId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== initialLearningFormatId) {
    t2 = [learningFormatAtom, initialLearningFormatId];
    $[5] = initialLearningFormatId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [campusResolutionSettledAtom, true];
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = [t1, t2, t3];
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useHydrateAtoms(t4);
  const [selectedLearningFormat, setSelectedLearningFormat] = useAtom(learningFormatAtom);
  const [sessionModalitySlug, setSessionModalitySlug] = useAtom(selectedModalitySlugAtom);
  const [selectedCampusId] = useAtom(selectedCampusAtom);
  const [campusResolutionSettled] = useAtom(campusResolutionSettledAtom);
  const keyedCopy = props.coursePageSection?.keyedCopy ?? EMPTY_KEYED_COPY;
  const courseId = props.course?._id ?? null;
  const appliedCourseIdRef = useRef(null);
  const appliedUrlSlugRef = useRef(null);
  const formatAutoScrollAllowedRef = useRef(initialLearningFormatId == null);
  const didFormatAutoScrollRef = useRef(false);
  const modalityRows = useModalityRows(props.course, selectedLearningFormat, props.campuses, selectedCampusId, props.campusCoursePrices, keyedCopy, props.campusLocationCoursePageSection);
  let t5;
  if ($[11] !== props.course?.courseOfferings || $[12] !== props.course?.sequenceType || $[13] !== selectedLearningFormat || $[14] !== setSelectedLearningFormat || $[15] !== setSessionModalitySlug) {
    t5 = modality => {
      if (selectedLearningFormat === modality._id) {
        return;
      }
      setSelectedLearningFormat(modality._id);
      setSessionModalitySlug(modality.slug.current);
      if (isBeastAcademyModality(modality)) {
        formatAutoScrollAllowedRef.current = false;
        return;
      }
      const shouldScroll = shouldAutoScrollOnSelection({
        allowAutoScroll: formatAutoScrollAllowedRef.current,
        alreadyScrolled: didFormatAutoScrollRef.current,
        previousSelectedId: selectedLearningFormat,
        nextSelectedId: modality._id
      });
      formatAutoScrollAllowedRef.current = false;
      if (!shouldScroll) {
        return;
      }
      didFormatAutoScrollRef.current = true;
      const target = getFormatAutoScrollTarget({
        sequenceType: props.course?.sequenceType,
        courseOfferings: props.course?.courseOfferings ?? [],
        selectedModalityId: modality._id
      });
      scrollToCourseSection(target);
    };
    $[11] = props.course?.courseOfferings;
    $[12] = props.course?.sequenceType;
    $[13] = selectedLearningFormat;
    $[14] = setSelectedLearningFormat;
    $[15] = setSessionModalitySlug;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  props.course?.courseOfferings;
  props.course?.sequenceType;
  const onSelectLearningFormat = t5;
  let t6;
  let t7;
  if ($[17] !== courseId || $[18] !== modalityRows || $[19] !== props.course || $[20] !== props.initialModalitySlug || $[21] !== sessionModalitySlug || $[22] !== setSelectedLearningFormat || $[23] !== setSessionModalitySlug) {
    t6 = () => {
      const previousCourseId = appliedCourseIdRef.current;
      const urlSlug = props.initialModalitySlug ?? null;
      const shouldApply = shouldApplyUrlPreference({
        previousCourseId,
        courseId,
        urlSlug,
        appliedUrlSlug: appliedUrlSlugRef.current
      });
      appliedCourseIdRef.current = courseId;
      if (!shouldApply) {
        return;
      }
      if (previousCourseId !== courseId) {
        didFormatAutoScrollRef.current = false;
        formatAutoScrollAllowedRef.current = true;
      }
      const trimmedUrl = urlSlug?.trim() || null;
      appliedUrlSlugRef.current = trimmedUrl;
      if (trimmedUrl) {
        setSessionModalitySlug(trimmedUrl);
      }
      const preferredSlug = resolvePreferredModalitySlug(trimmedUrl, sessionModalitySlug);
      const preferredId = getModalityIdBySlug(props.course, preferredSlug);
      if (preferredId) {
        const modality_0 = modalityRows.find(row => row.modality._id === preferredId)?.modality;
        if (modality_0) {
          sendDiscoveryEventBeacon("course_modality_selected", {
            course: props.course?.slug.current ?? "",
            modality: modality_0.slug.current,
            auto_select: true
          });
        }
      }
      setSelectedLearningFormat(preferredId);
      if (preferredId) {
        formatAutoScrollAllowedRef.current = false;
      }
    };
    t7 = [courseId, modalityRows, props.course, props.initialModalitySlug, sessionModalitySlug, setSelectedLearningFormat, setSessionModalitySlug];
    $[17] = courseId;
    $[18] = modalityRows;
    $[19] = props.course;
    $[20] = props.initialModalitySlug;
    $[21] = sessionModalitySlug;
    $[22] = setSelectedLearningFormat;
    $[23] = setSessionModalitySlug;
    $[24] = t6;
    $[25] = t7;
  } else {
    t6 = $[24];
    t7 = $[25];
  }
  useEffect(t6, t7);
  let t8;
  if ($[26] !== modalityRows[0] || $[27] !== modalityRows.length || $[28] !== props.course?.slug || $[29] !== selectedLearningFormat || $[30] !== setSelectedLearningFormat || $[31] !== setSessionModalitySlug) {
    t8 = () => {
      if (modalityRows.length !== 1) {
        return;
      }
      const row_0 = modalityRows[0];
      if (!row_0 || row_0.isSelectDisabled) {
        return;
      }
      formatAutoScrollAllowedRef.current = false;
      if (selectedLearningFormat === row_0.modality._id) {
        return;
      }
      setSelectedLearningFormat(row_0.modality._id);
      setSessionModalitySlug(row_0.modality.slug.current);
      sendDiscoveryEventBeacon("course_modality_selected", {
        course: props.course?.slug.current ?? "",
        modality: row_0.modality.slug.current,
        auto_select: true
      });
    };
    $[26] = modalityRows[0];
    $[27] = modalityRows.length;
    $[28] = props.course?.slug;
    $[29] = selectedLearningFormat;
    $[30] = setSelectedLearningFormat;
    $[31] = setSessionModalitySlug;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== modalityRows || $[34] !== props.course || $[35] !== selectedLearningFormat || $[36] !== setSelectedLearningFormat || $[37] !== setSessionModalitySlug) {
    t9 = [modalityRows, props.course, selectedLearningFormat, setSelectedLearningFormat, setSessionModalitySlug];
    $[33] = modalityRows;
    $[34] = props.course;
    $[35] = selectedLearningFormat;
    $[36] = setSelectedLearningFormat;
    $[37] = setSessionModalitySlug;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[39] !== campusResolutionSettled || $[40] !== modalityRows || $[41] !== setSelectedLearningFormat) {
    t10 = () => {
      if (!campusResolutionSettled) {
        return;
      }
      const selectedRow = modalityRows.find(_temp4);
      if (selectedRow?.isSelectDisabled) {
        setSelectedLearningFormat(null);
        if (!didFormatAutoScrollRef.current) {
          formatAutoScrollAllowedRef.current = true;
        }
      }
    };
    t11 = [campusResolutionSettled, modalityRows, setSelectedLearningFormat];
    $[39] = campusResolutionSettled;
    $[40] = modalityRows;
    $[41] = setSelectedLearningFormat;
    $[42] = t10;
    $[43] = t11;
  } else {
    t10 = $[42];
    t11 = $[43];
  }
  useEffect(t10, t11);
  const isStacked = useIsStackedLearningFormat();
  const linkToBAO = useLinkToBAO();
  if (isStacked) {
    let t12;
    if ($[44] !== linkToBAO || $[45] !== onSelectLearningFormat || $[46] !== props) {
      t12 = <LearningFormatMobile {...props} linkToBAO={linkToBAO} onSelectLearningFormat={onSelectLearningFormat} />;
      $[44] = linkToBAO;
      $[45] = onSelectLearningFormat;
      $[46] = props;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    return t12;
  }
  let t12;
  if ($[48] !== linkToBAO || $[49] !== onSelectLearningFormat || $[50] !== props) {
    t12 = <LearningFormatDesktop {...props} linkToBAO={linkToBAO} onSelectLearningFormat={onSelectLearningFormat} />;
    $[48] = linkToBAO;
    $[49] = onSelectLearningFormat;
    $[50] = props;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  return t12;
}
function _temp4(row_1) {
  return row_1.isSelected;
}